import type { ColDef, ICellRendererParams, ValueGetterParams, GetQuickFilterTextParams } from 'ag-grid-community'
import { Texto, Vertical, addCommasToNumber } from '@gravitate-js/excalibrr'
import { Tooltip } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import moment from 'moment'
import { DocumentNumberRenderer } from './renderers/DocumentNumberRenderer'
import { ManageMatchActionRenderer } from './renderers/ManageMatchActionRenderer'
import { ProductsCellRenderer } from '../renderers/ProductsCellRenderer'
import { getUserTimezone, formatters } from '@utils/intl/dateTimeFormatters'
import { formatVolume, formatVolumeConsistent } from '../detailColumnDefs/formatters'
import type { MatchBol } from '@api/useSupplierReconciliation/types/match'
import { calculateManageMatchPrecisions, type ColumnPrecision } from '../../utils/precisionCalculator'
import {
  createDualColumnFilter,
  createDualColumnFilterParams,
  createDateStatusDualColumnFilter,
  createDateStatusDualColumnFilterParams,
} from '../../../components/columnDefs/utils/filterUtils'
import { useFormatter } from '@api/useFormatter'

dayjs.extend(utc)
dayjs.extend(timezone)

// Currency cell renderer components that use market profile currency symbol
function BolProductTotalCurrencyRenderer({ value }: ICellRendererParams<MatchBol>) {
  const { formatCurrency } = useFormatter()
  const currencySymbol = formatCurrency(null, true)

  if (value == null || value === 0) {
    return (
      <Texto category='p2' appearance='hint'>
        —
      </Texto>
    )
  }

  const sign = value < 0 ? '-' : ''
  const formatted = `${sign}${currencySymbol}${addCommasToNumber(Math.abs(value), 2)}`
  return (
    <Texto category='p2' weight='500' align='right'>
      {formatted}
    </Texto>
  )
}

function BolTaxTotalCurrencyRenderer({ value }: ICellRendererParams<MatchBol>) {
  const { formatCurrency } = useFormatter()
  const currencySymbol = formatCurrency(null, true)

  if (value == null || value === 0) {
    return (
      <Texto category='p2' appearance='hint'>
        —
      </Texto>
    )
  }

  const sign = value < 0 ? '-' : ''
  const formatted = `${sign}${currencySymbol}${addCommasToNumber(Math.abs(value), 2)}`
  return (
    <Texto category='p2' align='right'>
      {formatted}
    </Texto>
  )
}

function BolTotalCurrencyRenderer({ value }: ICellRendererParams<MatchBol>) {
  const { formatCurrency } = useFormatter()
  const currencySymbol = formatCurrency(null, true)

  const amount = value || 0
  const sign = amount < 0 ? '-' : ''
  const formatted = `${sign}${currencySymbol}${addCommasToNumber(Math.abs(amount), 2)}`
  return (
    <Texto category='p2' weight='600' align='right'>
      {formatted}
    </Texto>
  )
}

interface HelperFunctions {
  isItemMatched: (item: MatchBol) => boolean
  getLinkedNumbers: (item: MatchBol) => string[]
}

interface ActionHandlers {
  handleAddToMatch: (type: 'invoice' | 'bol', item: MatchBol, itemId: string, matchId?: string) => void
  handleRemoveFromMatch: (type: 'invoice' | 'bol', itemId: string, item?: MatchBol) => void
  pendingItems: MatchBol[]
}

export function createBolColumns(
  helpers: HelperFunctions,
  actions: ActionHandlers,
  timezone?: string,
  precisions?: ColumnPrecision
): ColDef<MatchBol>[] {
  // Use provided precisions or fallback to defaults
  const columnPrecisions = precisions || { volume: 3, perUnit: 6, currency: 2 }
  return [
    {
      headerName: 'BOL #',
      width: 160,
      pinned: 'left',
      suppressSizeToFit: true,
      filter: createDualColumnFilter({
        field: 'bol_number',
        numberFieldTitle: 'Filter By BOL Number',
        statusFieldTitle: 'Filter By Status',
        valueGetter: (params: ValueGetterParams<MatchBol>) => {
          return params.data?.bol_number || ''
        },
        statusValueGetter: (params: ValueGetterParams<MatchBol>) => {
          // Use helper function to determine match status
          return helpers.isItemMatched(params.data!) ? 'Matched' : 'Unmatched'
        },
      }),
      filterParams: createDualColumnFilterParams({
        field: 'bol_number',
        numberFieldTitle: 'Filter By BOL Number',
        statusFieldTitle: 'Filter By Status',
        valueGetter: (params: ValueGetterParams<MatchBol>) => {
          return params.data?.bol_number || ''
        },
        statusValueGetter: (params: ValueGetterParams<MatchBol>) => {
          return helpers.isItemMatched(params.data!) ? 'Matched' : 'Unmatched'
        },
      }),
      valueGetter: (params: ValueGetterParams<MatchBol>) => {
        return params.data?.bol_number || ''
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<MatchBol>) => {
        const number = params.data?.bol_number || ''
        const status = helpers.isItemMatched(params.data!) ? 'Matched' : 'Unmatched'
        return `${number} ${status}`
      },
      cellRenderer: (params: ICellRendererParams<MatchBol>) => {
        return DocumentNumberRenderer({
          value: params.value,
          data: params.data,
          helpers,
        })
      },
    },
    {
      headerName: 'Date',
      field: 'bol_date',
      width: 110,
      filter: createDateStatusDualColumnFilter({
        field: 'bol_date',
        dateFieldTitle: 'Filter By BOL Date',
        statusFieldTitle: 'Filter By Status',
        statusValueGetter: (params: ValueGetterParams<MatchBol>) => {
          if (!params.data?.bol_date) return 'Missing'
          return 'Has Date'
        },
      }),
      filterParams: createDateStatusDualColumnFilterParams({
        field: 'bol_date',
        dateFieldTitle: 'Filter By BOL Date',
        statusFieldTitle: 'Filter By Status',
        statusValueGetter: (params: ValueGetterParams<MatchBol>) => {
          if (!params.data?.bol_date) return 'Missing'
          return 'Has Date'
        },
      }),
      valueGetter: (params: ValueGetterParams<MatchBol>) => {
        if (!params.data?.bol_date) return null
        // CRITICAL: Return the string directly, don't parse to Date here
        // Let the cellRenderer handle UTC parsing properly
        return params.data.bol_date
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<MatchBol>) => {
        const date = params.data?.bol_date
        if (!date) return 'Missing'

        // Use timezone from match context or user timezone
        const formatted = formatters.DATE(moment.utc(date), timezone, false)
        return `${formatted || dayjs.utc(date).tz(getUserTimezone()).format('MMM DD, YYYY')} Has Date`
      },
      cellRenderer: (params: ICellRendererParams<MatchBol>) => {
        const date = params.value
        if (!date) return '—'

        // Parse as UTC (moment.utc handles both Z and non-Z timestamps correctly)
        const formatted =
          formatters.DATE_TIME(moment.utc(date), timezone, false) ||
          dayjs.utc(date).tz(getUserTimezone()).format('MMM DD, YYYY - h:mm A')
        return <Texto category='p2'>{formatted}</Texto>
      },
    },
    {
      headerName: 'Order #',
      field: 'order_number',
      width: 120,
      filter: 'agTextColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      valueGetter: (params: ValueGetterParams<MatchBol>) => {
        return params.data?.order_number || ''
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<MatchBol>) => {
        return params.data?.order_number || ''
      },
      cellRenderer: (params: ICellRendererParams<MatchBol>) => {
        const orderNumber = params.value || params.data?.order_number

        if (!orderNumber)
          return (
            <Texto category='p2' appearance='hint'>
              —
            </Texto>
          )

        return (
          <Tooltip title={`Order: ${orderNumber}`}>
            <Texto category='p2' style={{ color: 'var(--theme-primary)' }}>
              {orderNumber}
            </Texto>
          </Tooltip>
        )
      },
    },
    {
      headerName: 'Product(s)',
      flex: 1.2,
      autoHeight: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      valueGetter: (params: ValueGetterParams<MatchBol>) => {
        const products = params.data?.products || []
        if (!products || products.length === 0) return ''

        const productNames = products.map((p: { product_name?: string }) => p.product_name).filter(Boolean)
        return productNames.join(', ')
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<MatchBol>) => {
        const products = params.data?.products || []
        if (!products || products.length === 0) return ''

        const productNames = products.map((p: { product_name?: string }) => p.product_name).filter(Boolean)
        return productNames.join(' ')
      },
      cellRenderer: ProductsCellRenderer,
    },
    {
      headerName: 'Net Volume',
      width: 120,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      valueGetter: (params: ValueGetterParams<MatchBol>) => {
        const products = params.data?.products || []
        if (!Array.isArray(products)) return 0

        // Sum up net volumes from products
        return products.reduce((sum: number, p: { net_volume?: number }) => sum + (p.net_volume || 0), 0)
      },
      cellRenderer: (params: ICellRendererParams<MatchBol>) => {
        const volume = params.value
        if (volume == null || volume === 0)
          return (
            <Texto category='p2' appearance='hint'>
              —
            </Texto>
          )

        const formatted = formatVolumeConsistent(volume, columnPrecisions.volume)
        return (
          <Texto category='p2' align='right'>
            {formatted}
          </Texto>
        )
      },
    },
    {
      headerName: 'Gross Volume',
      width: 120,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      valueGetter: (params: ValueGetterParams<MatchBol>) => {
        const products = params.data?.products || []
        if (!Array.isArray(products)) return 0

        // Sum up gross volumes from products
        return products.reduce((sum: number, p: { gross_volume?: number }) => sum + (p.gross_volume || 0), 0)
      },
      cellRenderer: (params: ICellRendererParams<MatchBol>) => {
        const volume = params.value
        if (volume == null || volume === 0)
          return (
            <Texto category='p2' appearance='hint'>
              —
            </Texto>
          )

        const formatted = formatVolumeConsistent(volume, columnPrecisions.volume)
        return (
          <Texto category='p2' align='right'>
            {formatted}
          </Texto>
        )
      },
    },
    {
      headerName: 'Product Total',
      width: 120,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      valueGetter: (params: ValueGetterParams<MatchBol>) => {
        // For BOL data, sum from products array
        if (params.data?.products) {
          return params.data.products.reduce(
            (sum: number, p: { product_total?: number }) => sum + (p.product_total || 0),
            0
          )
        }
        return 0
      },
      cellRenderer: BolProductTotalCurrencyRenderer,
    },
    {
      headerName: 'Tax Total',
      width: 110,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      valueGetter: (params: ValueGetterParams<MatchBol>) => {
        // For BOL data, sum from products array
        if (params.data?.products) {
          return params.data.products.reduce((sum: number, p: { tax_total?: number }) => sum + (p.tax_total || 0), 0)
        }
        return 0
      },
      cellRenderer: BolTaxTotalCurrencyRenderer,
    },
    {
      headerName: 'Total',
      width: 120,
      type: 'numericColumn',
      pinned: 'right',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      valueGetter: (params: ValueGetterParams<MatchBol>) => {
        // Calculate total as Product Total + Tax Total
        if (params.data?.products) {
          const productTotal = params.data.products.reduce(
            (sum: number, p: { product_total?: number }) => sum + (p.product_total || 0),
            0
          )
          const taxTotal = params.data.products.reduce(
            (sum: number, p: { tax_total?: number }) => sum + (p.tax_total || 0),
            0
          )
          return productTotal + taxTotal
        }
        // Fallback to bol_total if products array is not available
        return params.data?.bol_total || 0
      },
      cellRenderer: BolTotalCurrencyRenderer,
    },
    {
      headerName: 'Supplier',
      width: 150,
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      valueGetter: (params: ValueGetterParams<MatchBol>) => {
        return params.data?.supplier_name || 'Unknown'
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<MatchBol>) => {
        return params.data?.supplier_name || 'Unknown'
      },
      cellRenderer: (params: ICellRendererParams<MatchBol>) => {
        const supplier = params.value
        return (
          <Tooltip title={supplier}>
            <Texto
              category='p2'
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '130px',
              }}
            >
              {supplier}
            </Texto>
          </Tooltip>
        )
      },
    },
    {
      headerName: 'Terminal',
      width: 150,
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      valueGetter: (params: ValueGetterParams<MatchBol>) => {
        return params.data?.terminal_name || 'N/A'
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<MatchBol>) => {
        return params.data?.terminal_name || 'N/A'
      },
      cellRenderer: (params: ICellRendererParams<MatchBol>) => {
        const terminal = params.value
        return (
          <Tooltip title={terminal}>
            <Texto
              category='p2'
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '130px',
              }}
            >
              {terminal}
            </Texto>
          </Tooltip>
        )
      },
    },
    {
      headerName: 'Destination',
      width: 150,
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      valueGetter: (params: ValueGetterParams<MatchBol>) => {
        return params.data?.destination_name || 'N/A'
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<MatchBol>) => {
        return params.data?.destination_name || 'N/A'
      },
      cellRenderer: (params: ICellRendererParams<MatchBol>) => {
        const destination = params.value
        if (!destination || destination === 'N/A') {
          return (
            <Texto category='p2' appearance='hint'>
              N/A
            </Texto>
          )
        }
        return (
          <Tooltip title={destination}>
            <Texto
              category='p2'
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '130px',
              }}
            >
              {destination}
            </Texto>
          </Tooltip>
        )
      },
    },
    {
      headerName: 'Actions',
      width: 120,
      pinned: 'right',
      sortable: false,
      resizable: false,
      suppressMenu: true,
      cellRenderer: (params: ICellRendererParams<MatchBol>) => {
        return ManageMatchActionRenderer({
          data: params.data,
          actions,
          helpers,
          searchType: 'bol',
        })
      },
    },
  ]
}
